/**
 * Session-wide Create RMA state: the fee configuration, the automations and the internal note.
 *
 * The merchant configures these once per page and they apply to every RMA the session creates
 * (FR7a), so one instance is shared by every draft's RmaRequestStore rather than copied into each.
 * Fee values are derived from the union of all drafts' selected items, and the label-fee
 * capabilities are the intersection across drafts, so no draft is ever handed a fee type its own
 * return methods cannot honour.
 */

import { makeAutoObservable } from "mobx";
import { IOption } from "../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import { CREATE_RMA_MODE } from "../../../services/api/create-rma-service";
import {
	AMOUNT_TYPE,
	FEE_AMOUNT_OPTIONS_TRANSLATED,
	FEE_TYPE,
} from "../../policies/policy-types";
import {
	applyCreateRmaLabelFeeCapabilityDefaults,
	buildGodModeShippingLabelFeeOptions,
	getCreateRmaLabelFeeCapabilityEnforcement,
} from "../utils/label-fee-capability";
import CreateRmaSettingsStore from "./create-rma-settings-store";
import CreateRmaPolicyFeeDerivationStore from "./create-rma-policy-fee-derivation-store";
import RmaFeeStore from "./rma-fee-store";
import RmaFeesListStore from "./rma-fees-list-store";
import RmaAutoRefundStore, { IAutoRefundDto } from "./rma-auto-refund-store";
import type RequestProductStore from "./request-product-store";
/** type-only: the drafts register themselves, so no runtime edge back to the request store */
import type RmaRequestStore from "./rma-request-store";

/** the session-level part of ICreateRmaDto, carried by every RMA the session creates */
export interface ICreateRmaSessionDto {
	auto_mark_as_done: boolean;
	is_auto_approved: boolean;
	send_customer_emails: boolean;
	note?: string;
}

export type FeeKey = 'returnFee' | 'labelFee';

class CreateRmaSessionStore {
	private _requestStores: RmaRequestStore[] = [];
	private _feeDerivation: CreateRmaPolicyFeeDerivationStore;
	private _autoRefundStore: RmaAutoRefundStore;
	private _model: ICreateRmaSessionDto;

	constructor(private _settingsStore: CreateRmaSettingsStore) {
		makeAutoObservable(this, {}, { autoBind: true });

		this._model = {
			note: '',
			auto_mark_as_done: false,
			is_auto_approved: false,
			send_customer_emails: this._settingsStore.sendCustomerEmails,
		};

		this._autoRefundStore = new RmaAutoRefundStore(this._autoRefundDefaults());
		this._feeDerivation = new CreateRmaPolicyFeeDerivationStore(this._settingsStore);
		this._applyLabelFeeCapabilityDefaultsToStore(this._feeDerivation.feesListStore.labelFee);
		this.setDefaultsToAutomation();
	}

	/**
	 * Every draft's request store registers itself, so the session can derive its fee values from
	 * the whole selection. Registration order is draft order.
	 */
	public register(requestStore: RmaRequestStore): void {
		if (!this._requestStores.includes(requestStore)) {
			this._requestStores.push(requestStore);
		}
	}

	public unregister(requestStore: RmaRequestStore): void {
		this._requestStores = this._requestStores.filter((store) => store !== requestStore);
	}

	get feesListStore(): RmaFeesListStore { return this._feeDerivation.feesListStore; }
	get autoRefundStore(): RmaAutoRefundStore { return this._autoRefundStore; }
	get note(): string | undefined { return this._model.note; }
	get autoMarkAsDone(): boolean { return this._model.auto_mark_as_done; }
	get isAutoApprove(): boolean { return this._model.is_auto_approved; }
	get sendCustomerEmails(): boolean { return this._model.send_customer_emails; }
	get shouldDisableResetToDefault(): boolean {
		return this._model.auto_mark_as_done === this._settingsStore.autoMarkDone
			&& this._autoRefundStore.shouldDisableResetToDefault;
	}

	/** the drafts that contribute to the derivation: those with at least one selected item */
	private get _participatingStores(): RmaRequestStore[] {
		return this._requestStores.filter((store) => store.requestProducts.length > 0);
	}

	/** every selected item of the session, across all drafts */
	private get _selectedItems(): RequestProductStore[] {
		return this._requestStores.flatMap((store) => store.requestProducts);
	}

	/**
	 * The currency the fee amounts are entered in. Fees are one configuration for the session, so
	 * the first participating draft's currency labels the inputs.
	 */
	get paymentCurrencyCode(): string {
		const store = this._participatingStores[0] ?? this._requestStores[0];
		return store?.paymentCurrencyCode ?? '';
	}

	setNote(value: string): void { this._model.note = value; }
	setAutoMarkAsDone(value: boolean): void { this._model.auto_mark_as_done = value; }
	setIsAutoApprove(value: boolean): void { this._model.is_auto_approved = value; }
	setSendCustomerEmail(value: boolean): void { this._model.send_customer_emails = value; }

	public setDefaultsToAutomation(): void {
		this.setAutoMarkAsDone(this._settingsStore.autoMarkDone);
		this._autoRefundStore.setDefaults();
	}

	public requestModel(): ICreateRmaSessionDto {
		return { ...this._model };
	}

	public feeAmountTypeOptions(
		feeType: FEE_TYPE | null | undefined,
		getTranslation: (key: string, scope: string) => string,
		scope: string,
	): IOption[] {
		let options = FEE_AMOUNT_OPTIONS_TRANSLATED;
		if (feeType === FEE_TYPE.RETURN_FEE) {
			options = options.filter(o => [
				AMOUNT_TYPE.FIXED, AMOUNT_TYPE.ITEM_PERCENTAGE_PAID_PRICE
			].includes(o.value));
		}
		return this._translateIOptionArray(options, getTranslation, scope);
	}

	public shippingLabelFeesOptions(
		getTranslation: (key: string, scope: string) => string,
		scope: string,
	): IOption[] {
		const options = buildGodModeShippingLabelFeeOptions(this.isLabelCostSupported());
		return this._translateIOptionArray(options, getTranslation, scope);
	}

	/**
	 * A label-cost fee type is offered only when every participating draft supports it, so the one
	 * shared value stays valid for every RMA the session creates.
	 */
	public isLabelCostSupported(): boolean {
		const stores = this._participatingStores;
		if (stores.length === 0) {
			return false;
		}
		return stores.every((store) => store.isLabelCostSupported());
	}

	public hasNoShipment(): boolean {
		const items = this._selectedItems;
		if (items.length === 0) {
			return false;
		}
		return items.every((item) => item.hasNoShipmentRequiredRm());
	}

	/** one waived item waives the session's fees, the same way it waives a single order's today */
	public shouldWaveAllFees(): boolean {
		return this._selectedItems.some((item) => item.hasWaveAllFeesPolicyRule());
	}

	public doesLabelFeeIncludeCost(): boolean {
		const feeType = this._feeDerivation.feesListStore.labelFee.feeType;
		if (!feeType) {
			return false;
		}
		return [FEE_TYPE.COST_PLUS_SHIPMENT_FEE, FEE_TYPE.COST, FEE_TYPE.COST_PLUS_ITEM_FEE].includes(feeType);
	}

	/** re-derives the fee values from the whole selection; called whenever any draft's items change */
	public recalculateFees(): void {
		/** existing functionality for G-mode: */
		if (this._settingsStore.cRmaMode === CREATE_RMA_MODE.C_RMA_GOD_MODE) {
			this._resetLabelFeeStore();
			this._recalculateDraftFees();
			return;
		}

		const items = this._selectedItems;

		this._feeDerivation.deriveFromItems(items, {
			isLabelCostSupported: this.isLabelCostSupported(),
			shouldWaveAllFees: this.shouldWaveAllFees(),
		});

		this._recalculateDraftFees();
	}

	private _recalculateDraftFees(): void {
		this._requestStores.forEach((store) => store.recalculateOwnFees());
	}

	private _autoRefundDefaults(): IAutoRefundDto {
		return {
			is_on: this._settingsStore.isAutoRefund,
			trigger: this._settingsStore.autoRefundTrigger,
			validated_qualifier: this._settingsStore.autoRefundValidatedQualifier,
			execute: this._settingsStore.autoRefundExecute,
			since_value: this._settingsStore.autoRefundSinceValue,
			since_unit: this._settingsStore.autoRefundSinceUnit,
		};
	}

	private _applyLabelFeeCapabilityDefaultsToStore(labelFeeStore: RmaFeeStore): void {
		applyCreateRmaLabelFeeCapabilityDefaults(
			{
				isCharged: labelFeeStore.isCharged,
				feeType: labelFeeStore.feeType,
				isPolicyLedMode: this._settingsStore.cRmaMode !== CREATE_RMA_MODE.C_RMA_GOD_MODE,
			},
			{
				setCharged: labelFeeStore.setCharged,
				setFeeType: labelFeeStore.setFeeType,
			},
			getCreateRmaLabelFeeCapabilityEnforcement(this.isLabelCostSupported()),
		);
	}

	private _translateIOptionArray = (
		options: IOption[],
		getTranslation: (key: string, scope: string) => string,
		scope: string,
	) => {
		return options.map(option => {
			return {
				...option,
				label: getTranslation(option.label as string, scope)
			}
		});
	}

	private _resetLabelFeeStore(): void {
		if ((!this.isLabelCostSupported() && this.doesLabelFeeIncludeCost()) || this.hasNoShipment()) {
			this._feeDerivation.feesListStore.labelFee.resetStore();
		}
	}
}

export default CreateRmaSessionStore;
